import { drawChronClock, drawDuodClock } from "./clock-widgets.js";
import {
  dayProgress,
  dozenalFromUnits,
  formatHms,
  formatHmsColon,
  parseDozenal,
  parseHms,
  toDozenal,
  SECONDS_PER_DAY,
  UNITS_PER_DAY,
} from "./time.js";

export type ClockKind = "chron" | "duod";

const CHRON_HTML = `
<div class="cd-section">
    <h2 class="cd-title">Metric Time <span class="cd-dim">// chron</span></h2>
    <p class="cd-desc">Metric time divides the day into 100 equal parts called <span class="cd-hl">chrons</span>. Each unit scales by factors of 10, just like the metric system.</p>
    <table class="cd-table">
        <tr><th>Unit</th><th>Duration</th><th>Equivalent</th><th style="text-align: right">Value</th></tr>
        <tr><td class="cd-hl">hectochron</td><td>100 chrons</td><td>= 1 day</td><td id="val-hectochron" class="cd-value"></td></tr>
        <tr><td class="cd-hl">dekachron</td><td>10 chrons</td><td>= 2h 24m</td><td id="val-dekachron" class="cd-value">-</td></tr>
        <tr><td class="cd-hl">chron</td><td>1% of day</td><td>= 14m 24s</td><td id="val-chron" class="cd-value">-</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="dice">decichron</td><td>0.1 chron</td><td>= 1m 26.4s</td><td id="val-decichron" class="cd-value">-</td></tr>
        <tr><td class="cd-hl">centichron</td><td>0.01 chron</td><td>= 8.64s</td><td id="val-centichron" class="cd-value">-</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="milk">millichron</td><td>0.001 chron</td><td>= 864ms</td><td id="val-millichron" class="cd-value">-</td></tr>
    </table>
</div>
<div class="cd-section">
    <h3 class="cd-subtitle">Convert</h3>
    <div class="cd-converter">
        <div class="cd-input-group">
            <input type="number" id="chron-input" class="cd-input" placeholder="0" step="any">
            <label class="cd-label">chrons</label>
        </div>
        <span class="cd-arrow">⇄</span>
        <div class="cd-input-group">
            <input type="text" id="chron-output" class="cd-input" placeholder="0h 0m 0s" readonly>
            <label class="cd-label">hours / min / sec</label>
        </div>
    </div>
    <div class="cd-converter">
        <div class="cd-input-group">
            <input type="text" id="imperial-input" class="cd-input" placeholder="0:00:00">
            <label class="cd-label">HH:MM:SS</label>
        </div>
        <span class="cd-arrow">⇄</span>
        <div class="cd-input-group">
            <input type="text" id="imperial-output" class="cd-input" placeholder="0.000" readonly>
            <label class="cd-label">chrons</label>
        </div>
    </div>
</div>`;

const DUOD_HTML = `
<div class="cd-section">
    <h2 class="cd-title">Duodecimal Time <span class="cd-dim">// duod</span></h2>
    <p class="cd-desc">Duodecimal time expresses the day as a base-12 fraction from <span class="cd-hl">.00000</span> (midnight) to <span class="cd-hl">.εεεεε</span> (end of day). Uses the <span class="cd-hl">Primel</span> system of units.</p>
    <table class="cd-table cd-table-wide">
        <tr><th>Quantitel</th><th>Colloquial</th><th>SI Equiv</th><th>Base 12</th><th style="text-align: right">Value</th></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="h↑tml">hexqua·timel</td><td class="cd-hover" data-tooltip="dy">day</td><td>86,400s</td><td>42,000<sub>z</sub>s</td><td id="val-duod-0" class="cd-value">0</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="p↑tml">pentqua·timel</td><td class="cd-hover" data-tooltip="dwell">dwell</td><td>7,200s = 2h</td><td>4,200<sub>z</sub>s</td><td id="val-duod-1" class="cd-value">-</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="q↑tml">quadqua·timel</td><td class="cd-hover" data-tooltip="breather">breather</td><td>600s = 10m</td><td>420<sub>z</sub>s</td><td id="val-duod-2" class="cd-value">-</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="t↑tml">triqua·timel</td><td class="cd-hover" data-tooltip="tr">trice</td><td>50s</td><td>42<sub>z</sub>s</td><td id="val-duod-3" class="cd-value">-</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="b↑tml">biqua·timel</td><td class="cd-hover" data-tooltip="lull">lull</td><td>≈4.17s</td><td>4.2<sub>z</sub>s</td><td id="val-duod-4" class="cd-value">-</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="u↑tml">unqua·timel</td><td class="cd-hover" data-tooltip="tw">twinkling</td><td>≈0.347s</td><td>0.42<sub>z</sub>s</td><td id="val-duod-5" class="cd-value">-</td></tr>
        <tr><td class="cd-hl cd-hover" data-tooltip="tml">timel</td><td class="cd-hover" data-tooltip="vibe">vibe</td><td>≈0.029s</td><td>0.042<sub>z</sub>s</td><td id="val-duod-6" class="cd-value"></td></tr>
    </table>
</div>
<div class="cd-section">
    <h3 class="cd-subtitle">Convert</h3>
    <div class="cd-converter">
        <div class="cd-input-group">
            <input type="text" id="duod-input" class="cd-input" placeholder=".00000" maxlength="6">
            <label class="cd-label">duodecimal (base 12)</label>
        </div>
        <span class="cd-arrow">⇄</span>
        <div class="cd-input-group">
            <input type="text" id="duod-output" class="cd-input" placeholder="0:00:00" readonly>
            <label class="cd-label">hours / min / sec</label>
        </div>
    </div>
    <div class="cd-converter">
        <div class="cd-input-group">
            <input type="text" id="duod-imp-input" class="cd-input" placeholder="0:00:00">
            <label class="cd-label">HH:MM:SS</label>
        </div>
        <span class="cd-arrow">⇄</span>
        <div class="cd-input-group">
            <input type="text" id="duod-imp-output" class="cd-input" placeholder=".00000" readonly>
            <label class="cd-label">duodecimal (base 12)</label>
        </div>
    </div>
</div>`;

let activeKind: ClockKind | null = null;
let frame = 0;
let tooltip: HTMLElement | null = null;

export const getActiveClock = (): ClockKind | null => activeKind;

function element<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function ensureTooltip(): HTMLElement {
  tooltip ??= element("uptime-tooltip");
  if (!tooltip) {
    tooltip = document.createElement("div");
    tooltip.id = "uptime-tooltip";
    document.body.appendChild(tooltip);
  }
  return tooltip;
}

/** Small glosses attached to the unit names in the tables. */
function hookTooltips(): void {
  const box = ensureTooltip();
  const content = element("clock-detail-content");
  if (!content) return;

  for (const target of Array.from(content.querySelectorAll<HTMLElement>(".cd-hover"))) {
    const show = (event: MouseEvent) => {
      const text = target.getAttribute("data-tooltip");
      if (!text) return;
      box.textContent = text;
      box.classList.add("visible");
      position(event);
    };
    const position = (event: MouseEvent) => {
      box.style.left = `${event.clientX}px`;
      box.style.top = `${event.clientY - 8}px`;
    };
    const hide = () => box.classList.remove("visible");

    target.addEventListener("mouseenter", show);
    target.addEventListener("mousemove", position);
    target.addEventListener("mouseleave", hide);
  }
}

function updateLiveValues(): void {
  const progress = dayProgress();

  if (activeKind === "chron") {
    const digits = (progress * 100).toFixed(3).padStart(6, "0");
    const ids = ["dekachron", "chron", "decichron", "centichron", "millichron"];
    const values = [digits[0], digits[1], digits[3], digits[4], digits[5]];
    ids.forEach((id, index) => {
      const cell = element(`val-${id}`);
      if (cell) cell.textContent = values[index] ?? "-";
    });
    return;
  }

  const dozenal = toDozenal(progress);
  for (let i = 0; i < 5; i++) {
    const cell = element(`val-duod-${i + 1}`);
    if (cell) cell.textContent = dozenal[i] ?? "-";
  }
}

function wireChronConverters(): void {
  const chronIn = element<HTMLInputElement>("chron-input");
  const chronOut = element<HTMLInputElement>("chron-output");
  const hmsIn = element<HTMLInputElement>("imperial-input");
  const hmsOut = element<HTMLInputElement>("imperial-output");

  chronIn?.addEventListener("input", () => {
    const value = Number.parseFloat(chronIn.value);
    if (chronOut) chronOut.value = Number.isFinite(value) ? formatHms(value * 864) : "";
  });

  hmsIn?.addEventListener("input", () => {
    const seconds = parseHms(hmsIn.value);
    if (hmsOut) hmsOut.value = seconds === null ? "" : (seconds / 864).toFixed(3);
  });
}

function wireDuodConverters(): void {
  const duodIn = element<HTMLInputElement>("duod-input");
  const duodOut = element<HTMLInputElement>("duod-output");
  const hmsIn = element<HTMLInputElement>("duod-imp-input");
  const hmsOut = element<HTMLInputElement>("duod-imp-output");

  duodIn?.addEventListener("input", () => {
    const units = parseDozenal(duodIn.value);
    if (duodOut) {
      duodOut.value = units === null ? "" : formatHmsColon((units / UNITS_PER_DAY) * SECONDS_PER_DAY);
    }
  });

  hmsIn?.addEventListener("input", () => {
    const seconds = parseHms(hmsIn.value);
    if (hmsOut) {
      hmsOut.value =
        seconds === null ? "" : dozenalFromUnits((seconds / SECONDS_PER_DAY) * UNITS_PER_DAY);
    }
  });
}

/** Tab cycles the editable fields rather than escaping to browser chrome. */
function handleTab(event: KeyboardEvent): void {
  if (!activeKind || event.key !== "Tab") return;
  const content = element("clock-detail-content");
  if (!content) return;

  const inputs = Array.from(content.querySelectorAll<HTMLInputElement>(".cd-input:not([readonly])"));
  if (inputs.length === 0) return;

  event.preventDefault();
  const current = inputs.indexOf(document.activeElement as HTMLInputElement);
  const next = event.shiftKey
    ? current <= 0
      ? inputs.length - 1
      : current - 1
    : current < 0 || current >= inputs.length - 1
      ? 0
      : current + 1;
  inputs[next]?.focus();
}

function renderLoop(): void {
  const canvas = element<HTMLCanvasElement>("clock-detail-canvas");
  if (canvas && activeKind && canvas.clientWidth > 0 && canvas.clientHeight > 0) {
    if (activeKind === "chron") drawChronClock(canvas, true);
    else drawDuodClock(canvas, true);
    updateLiveValues();
  }
  frame = requestAnimationFrame(renderLoop);
}

export interface OpenOptions {
  instant?: boolean;
  /** Prefills the HH:MM:SS converter from a /chron/12:00 style deep link. */
  deepLink?: string;
}

export function openClockDetail(kind: ClockKind, options: OpenOptions = {}): void {
  const detail = element("clock-detail");
  const content = element("clock-detail-content");
  const canvas = element<HTMLCanvasElement>("clock-detail-canvas");
  if (!detail || !content || !canvas) return;

  activeKind = kind;
  window.addEventListener("keydown", handleTab);

  content.innerHTML = kind === "chron" ? CHRON_HTML : DUOD_HTML;
  if (kind === "chron") wireChronConverters();
  else wireDuodConverters();

  if (options.deepLink) {
    const input = element<HTMLInputElement>(kind === "chron" ? "imperial-input" : "duod-imp-input");
    if (input) {
      input.value = options.deepLink;
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  content.classList.remove("cd-content-visible");
  document.body.style.overflow = "hidden";
  detail.classList.remove("hidden");

  if (frame) cancelAnimationFrame(frame);
  renderLoop();

  const reveal = () => {
    detail.classList.add("visible");
    content.classList.add("cd-content-visible");
    hookTooltips();
  };

  if (options.instant) {
    document.documentElement.classList.remove("init-clock");
    reveal();
    return;
  }

  const widget = document.querySelector<HTMLElement>(`.clock-widget[data-clock="${kind}"]`);
  if (!widget) {
    requestAnimationFrame(() => requestAnimationFrame(reveal));
    return;
  }

  // The widget appears to grow into the detail view: the canvas starts at the
  // widget's bounds and animates to the panel's.
  const from = widget.getBoundingClientRect();
  Object.assign(canvas.style, {
    position: "fixed",
    top: `${from.top}px`,
    left: `${from.left}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    margin: "0",
    borderRadius: "6px",
    zIndex: "950",
    transition: "none",
  });
  detail.style.opacity = "0";

  requestAnimationFrame(() => {
    const inner = element("clock-detail-inner");
    const to = inner?.getBoundingClientRect();
    if (!to) {
      settle(canvas, detail, reveal);
      return;
    }

    canvas.style.transition =
      "top 0.45s cubic-bezier(0.4, 0, 0.2, 1), left 0.45s cubic-bezier(0.4, 0, 0.2, 1), width 0.45s cubic-bezier(0.4, 0, 0.2, 1), height 0.45s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.45s cubic-bezier(0.4, 0, 0.2, 1)";
    canvas.style.top = `${to.top + 40}px`;
    canvas.style.left = `${to.left}px`;
    canvas.style.width = `${to.width}px`;
    canvas.style.height = "300px";
    canvas.style.borderRadius = "8px";

    detail.style.transition = "opacity 0.35s ease 0.1s";
    detail.style.opacity = "1";

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      settle(canvas, detail, reveal);
    };

    canvas.addEventListener("transitionend", function onEnd(event) {
      if (!["top", "width", "height"].includes(event.propertyName)) return;
      canvas.removeEventListener("transitionend", onEnd);
      finish();
    });
    window.setTimeout(finish, 550);
  });
}

function settle(canvas: HTMLCanvasElement, detail: HTMLElement, reveal: () => void): void {
  canvas.removeAttribute("style");
  detail.style.transition = "";
  detail.style.opacity = "";
  reveal();
}

export function closeClockDetail(): void {
  const detail = element("clock-detail");
  if (!detail || detail.classList.contains("hidden")) return;

  const content = element("clock-detail-content");
  const canvas = element<HTMLCanvasElement>("clock-detail-canvas");
  window.removeEventListener("keydown", handleTab);
  content?.classList.remove("cd-content-visible");

  const widget = activeKind
    ? document.querySelector<HTMLElement>(`.clock-widget[data-clock="${activeKind}"]`)
    : null;

  const cleanup = () => {
    detail.classList.add("hidden");
    canvas?.removeAttribute("style");
    activeKind = null;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };

  if (!widget || !canvas) {
    detail.classList.remove("visible");
    window.setTimeout(cleanup, 300);
    document.body.style.overflow = "";
    return;
  }

  const to = widget.getBoundingClientRect();
  const from = canvas.getBoundingClientRect();

  Object.assign(canvas.style, {
    position: "fixed",
    top: `${from.top}px`,
    left: `${from.left}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    margin: "0",
    zIndex: "950",
    transition: "none",
  });
  detail.classList.remove("visible");

  requestAnimationFrame(() => {
    canvas.style.transition =
      "top 0.4s cubic-bezier(0.4, 0, 0.2, 1), left 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease";
    canvas.style.top = `${to.top}px`;
    canvas.style.left = `${to.left}px`;
    canvas.style.width = `${to.width}px`;
    canvas.style.height = `${to.height}px`;
    canvas.style.borderRadius = "6px";
    canvas.style.opacity = "0.5";

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      cleanup();
    };

    canvas.addEventListener("transitionend", function onEnd(event) {
      if (!["top", "width", "height"].includes(event.propertyName)) return;
      canvas.removeEventListener("transitionend", onEnd);
      finish();
    });
    window.setTimeout(finish, 500);
  });

  document.body.style.overflow = "";
}
